import { useTranslation } from 'react-i18next';
import { AlertTriangle, Sparkles } from 'lucide-react';
import type { ExtractionProvenance } from '@/lib/contracts/types';

/**
 * What produced this draft, said plainly above the form.
 *
 * ## Why this component exists
 *
 * The endpoint behind it used to return a hardcoded contract — a confident
 * "Extracted Hotel (from PDF)" at USD 135 — for every upload, and the UI
 * rendered it exactly as it renders a real reading. There was no way to tell
 * the difference without reading the server, which is how a stub survived in a
 * demoed feature.
 *
 * So the two states are deliberately not the same shape:
 *
 * - **`model === 'mock'`** — no reader is configured. Amber, named, and it says
 *   the file was not read. There is nothing to review, because every field came
 *   back blank.
 * - **a real model** — names the model, shows its confidence, and, crucially,
 *   lists the fields **the document never stated**. Those arrive blank rather
 *   than guessed, so the warning is the only thing standing between an
 *   unstated rate and a saved rate-of-record.
 */
export function ExtractionBanner({ provenance }: { provenance: ExtractionProvenance }) {
  const { t } = useTranslation('network');
  const isMock = provenance.model === 'mock';

  if (isMock) {
    return (
      <div
        role="status"
        className="flex gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
        <div>
          <p className="font-semibold text-amber-700 dark:text-amber-400">
            {t('contracts.new.mockTitle')}
          </p>
          <p className="mt-1 text-muted-foreground">{t('contracts.new.mockBody')}</p>
          {provenance.note ? (
            <p className="mt-1 text-muted-foreground/80">{provenance.note}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      className="flex gap-2 rounded-md border border-dashed border-primary/40 bg-primary/5 p-3 text-xs"
    >
      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0">
        <p className="font-medium text-foreground/80">
          {t('contracts.new.draftTitle')}
          <span className="ml-2 font-normal text-muted-foreground">
            {t('contracts.new.readBy', {
              model: provenance.model,
              confidence: Math.round(provenance.confidence * 100),
            })}
          </span>
        </p>
        <p className="mt-1 text-muted-foreground">{t('contracts.new.draftBody')}</p>

        {provenance.missing.length > 0 ? (
          <p className="mt-2 font-medium text-amber-700 dark:text-amber-400">
            {t('contracts.new.missingFields', {
              count: provenance.missing.length,
              fields: provenance.missing
                .map((f) => t(`contracts.fields.${f}`, { defaultValue: f }))
                .join(', '),
            })}
          </p>
        ) : null}

        {provenance.note ? (
          <p className="mt-1 text-muted-foreground/80">{provenance.note}</p>
        ) : null}
      </div>
    </div>
  );
}
